import React from 'react';
import { connect } from 'react-redux';
import { Input, Form } from 'semantic-ui-react';
import { useIntl } from 'react-intl';
import { findError, validationClasses } from '@/utils/errors';
import findResult, { isEmpty } from '@/utils/resultsHelper';
import { states } from '@/constants/lifecycle';
import fields, { globalFieldLengths } from '@/constants/fields';
import { configurationCodes } from '@/configurations/constants';
import { fetchConfiguration } from '@/configurations/actions';
import FormValidationError from '@/components/errors/FormValidationError';
import fetch, { fetchConfig } from '@/hooks/fetch';
import SelectDatasourceType from '../datasource/containers/SelectDatasourceType';
import { updateDatasetProperties } from '../actions';
import CatalogueTemplatesDropdown from '../catalogue/templates/components/CatalogueTemplatesDropdown';
import { datasetSettingsFields } from '../settings/state/state';
import CatalogueTitleInput from '../catalogue/components/CatalogueTitleInput';
import { resolveCatalogueTemplate } from '../catalogue/helper';
import { datasetDirtyFlags, datasetSourceAndStepAttributes } from '../state/state';
import { datasetPerms } from '../sharing/state/state';
import { canChangeDatasetFoundations } from '../helper';

const SelectDatasetType = ({
  allowConfigurationRead,
  canChangeDatasource,
  canChangeTemplate,
  category,
  code,
  compact,
  datasetCode,
  defaultTemplate,
  fetchDefaultTemplate,
  isNew,
  originalTemplateCode,
  readStatus,
  saveErrors,
  templateCode,
  updateProperties,
}) => {
  const intl = useIntl();
  fetch.useFetchByCode(
    fetchDefaultTemplate,
    'all',
    configurationCodes.DEFAULT_CATALOGUE,
    readStatus,
    fetchConfig([defaultTemplate], () => allowConfigurationRead),
  );

  const updateCatalogueTemplate = (t) => {
    const props = {
      settings: { catalogue_definition_code: t.code },
      steps: { templateName: t.name },
    };
    if (isEmpty(originalTemplateCode)) {
      props.steps.templateCode = templateCode;
    }
    updateProperties(category, code, props);
  };

  const codeError = findError(saveErrors, 'catalogue', [fields.CODE]);
  const nameError = findError(saveErrors, 'catalogue', [fields.NAME]);
  const templateError = findError(saveErrors, 'catalogue', [fields.TEMPLATE]);

  return (
    <div className="aridhia-select-dataset-type aridhia-wizard-content">
      <Form autoComplete="off">
        <FormValidationError
          error={codeError}
          htmlFor="dataset_code"
          labelId="save.dataset.code"
        >
          <Input
            id="dataset_code"
            disabled={!isNew}
            type="custom"
            placeholder={intl.formatMessage({ id: 'save.dataset.code.placeholder' })}
            value={datasetCode || ''}
            maxLength={globalFieldLengths.code}
            onChange={(e) => updateProperties(category, code, { steps: { code: e.target.value } })}
            className={validationClasses(codeError, 'aridhia-form-input primary aridhia-is-edit-mode')}
          />
        </FormValidationError>
        <FormValidationError
          error={nameError}
          htmlFor="dataset_name"
          labelId="save.dataset.name"
        >
          <CatalogueTitleInput id="dataset_name" autoFocus category={category} code={code} />
        </FormValidationError>
        {canChangeTemplate && (
        <FormValidationError
          error={templateError}
          htmlFor="dataset_template"
          labelId="dataset.settings.catalogue.template.field.label"
        >
          <CatalogueTemplatesDropdown
            id="dataset_template"
            category="lookups"
            className="min-w-230"
            onChange={updateCatalogueTemplate}
            template={templateCode || defaultTemplate}
            withDescription
          />
        </FormValidationError>
        )}
        {canChangeDatasource && (
        <SelectDatasourceType category={category} code={code} compact={compact} isNew={isNew} />
        )}
      </Form>
    </div>
  );
};

const mapStateToProps = ({
  auth: { role: { permissions } },
  catalogues: { templates },
  configurations: { configurations },
  datasets: { datasets },
}, ownProps) => {
  const defaultTemplate = findResult(configurations, 'all', configurationCodes.DEFAULT_CATALOGUE) || {
    value: { value: '' },
    readStatus: states.NONE,
  };
  const { dataset } = resolveCatalogueTemplate(datasets, templates, ownProps, {
    isDirty: datasetDirtyFlags(),
    isNew: false,
    permissions: datasetPerms(),
    saveErrors: [],
    steps: datasetSourceAndStepAttributes(),
    setting: datasetSettingsFields(),
  });
  const {
    isNew, steps, saveErrors, code, settings,
  } = dataset;

  return (
    {
      allowConfigurationRead: permissions.configurations.read,
      ...canChangeDatasetFoundations(dataset, permissions),
      datasetCode: isNew ? steps.code : code,
      defaultTemplate: defaultTemplate.value.value,
      isNew,
      originalTemplateCode: steps.templateCode,
      readStatus: defaultTemplate.readStatus,
      saveErrors,
      templateCode: settings.catalogue_definition_code,
    }
  );
};

const mapDispatchToProps = {
  fetchDefaultTemplate: fetchConfiguration,
  updateProperties: updateDatasetProperties,
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectDatasetType);
